import { FaceItemModel, Facet } from '../../models/filter.model';
import { FantazPass } from '../game-history';
import { Game } from '../game/game.model';
import { Location } from '../location';
import { User } from '../user/user.model';
import { NextEvent } from './tournament';

export interface TournamentPlayed {
  /** Tournament name/identifier */
  tournamentName: string;

  /** Current status of the tournament */
  status: string;

  /** Number of times the user has played this tournament */
  timesPlayed: number;
}

export interface TournamentsPlayedRequest {
  userId: string;
  startAt?: string;
  endAt?: string;
}

export interface TournamentsPlayedResponse {
  tournaments?: TournamentPlayed[];
  total?: number;
  error?: string | null;
}

export interface CasualMatchParameters {
  eventCode: string | undefined;
  requiredEventCode?: boolean;
  scheduleSelected: EventSchedule;
  openTournament: CasualTournamentDetails;
  user: User;
  pass?: FantazPass;
  userLocation?: {
    deviceGPS?: Location | null;
    userAllowToUseFallbackLocation?: boolean | null;
  };
  playInstanceUuid?: string;
  retry?: boolean;
}
export interface BeginMatchRequest {
  type: string;
  eventPrototypeUuid: string;
  eventScheduleUuid: string;
  gameType: string;
  nonce?: string;
  action: string;
  retry?: boolean;
  devToolIsOpen?: boolean;
  monitoringSystems?: {
    gtmBlocked: boolean;
    smartlookBlocked: boolean;
    clarityBlocked: boolean;
  };
  playInstanceUuid?: string;
  userLocation?: {
    deviceGPS?: Location | null;
    userAllowToUseFallbackLocation?: boolean | null;
  };
  passUuid?: string;
  eventCode?: string;
  requiredEventCode?: boolean;
  version?: string;
  passcodes?: string[];
}

export class BeginMatchResponseSuccess {
  assetsUrl: string;
  eventSeed: number;
  gameKey: string;
  playInstanceUuid: string;
  eventInstanceUuid: string;
  needRetry: boolean;
}

export interface CasualTournamentEntity {
  tags: string[];
  id: string | number; // Primary ID
  name: string;
  description: string;
  uuid: string;
  url: string;
  enabled: boolean;
  streamEnabled: boolean;
  tag: string;
  game: Game;
  isComplete: boolean;
  eventSchedules: EventSchedule[];
  prizeRange: {
    min: number;
    max: number;
  };
  entryFeeRange: {
    start: number;
    end: number;
  };
  lifeExperience: boolean;
  prizeDetails?: PrizeDetails;
  comingSoon?: boolean;
  ended?: boolean;
  casualGameStartTime?: Date;
  casualGameEndTime?: Date;
  advertisement: boolean;
  metadata?: Metadata;
  room?: EventSchedule;
  rooms?: { [k: string]: number };
  tournamentCardBackground?: string;
  eventInstanceUuid?: string;
  fullSizeBannerImage?: string;
  tournamentFullSizeBanner?: string;
  tournamentMobileSizeBanner?: string;
  tournamentShareImage?: string;
  zboOnly?: boolean;
}

export interface EventSchedule {
  toPlayNow?: NextEvent | null;
  uuid: string;
  corporation: string;
  entryAmount: number;
  lifeExperience: boolean;
  pool: string;
  rakeScheduleUuid: string;
  setPerInstance: number;
  uniLevel: string;
  eventScheduleUuid: string;
  room: number;
  prize: number;
  retired: boolean;
  subscriptionStatus?: boolean;
  tag: string;
  totalEntryCount: number;
  prizeDetails: PrizeDetails[];
  firstPrize?: PrizeDetails;
  referralDetails?: UniLevelDetail[];
  seatsPerInstance?: number;
}

/**
 * Interface for the 'CasualTournamentDetails' data
 */
export interface CasualTournamentDetails {
  uuid: string; // Primary ID
  name: string;
  entryPoint: string;
  eventPrototypeUuid: string;
  eventInstanceUuid?: string;
  timesCanEnter: number;
  url: string;
  lifeExperience?: boolean;
  rating: number;
  stable: boolean;
  comingSoon?: boolean;
  isCompleted?: boolean;
  rooms: any;
  game: Game;
  description?: string;
  events: EventSchedule[];
  detailsForEventSchedule: EventSchedule[];
  metadata?: Metadata;
  casualGameEndTime: string;
  casualGameStartTime: string;
  fullSizeBannerImage: string;
  mobileSizeBannerImage: string;
  tournamentShareImage: string;
  prizeDetails?: PrizeDetails;
  advertisement: boolean;
  zboOnly?: boolean;
  isFree?: boolean;
  isForceFinalize?: boolean;

  // UI Properties
  endTime?: Date;
  isAvailable?: boolean;
  tournamentFullSizeBanner?: string;
}

type ImageFile = {
  ext: string;
  hash: string;
  height: number;
  isUrlSigned: true;
  mime: string;
  name: string;
  path: null;
  size: number;
  sizeInBytes: number;
  url: string;
  width: number;
};

export type ImageEntity = ImageFile & {
  id: string;
  formats?: {
    thumbnail?: ImageFile;
    lg?: ImageFile;
    md?: ImageFile;
    sm?: ImageFile;
    xs?: ImageFile;
  };
};

export interface Metadata {
  [key: string]: string | boolean | string[] | undefined | null | Object;
  browser?: string;
  experience?: string;
  format?: string;
  gameImage?: string;
  gameLongDescription?: string;
  gameName?: string;
  gameShortDescription?: string;
  system?: string;
  tournamentCardDescription?: string;
  tournamentOverview?: string;
  tournamentRules?: string;
  vipVideo?: string;
  tags?: string[];
  prizeValue?: string | null | undefined;
  internalUseOnly?: 'true' | 'false';
  fullSizeBannerImage?: string;
  mediumSizeBannerImage?: string;
  mobileSizeBannerImage?: string;
  thumbnailBannerImage?: string;

  //
  bannerImages?: {
    desktop?: ImageEntity;
    mobile?: ImageEntity;
    tablet?: ImageEntity;
  };

  cardImage?: ImageEntity;
}

export interface PrizeDetails {
  prizeType: string;
  prizeValue: number;
  rank: number;
  advertisement?: boolean;
  tangible?: boolean;
  technicalDetails?: string;
  prizeTemplateVipMetadata?: string[];
  prizeDetails?: string;
  prizeImageAvatar?: string | ImageEntity;
  prizeImageFullSize?: string | ImageEntity;
  prizeImageCard?: string | ImageEntity;
  prizeName?: string;
  prizeImageLayout: string;
}
export interface UniLevelDetail {
  amountPerUser: number;
  level: number;
  percent: number;
  totalAmount: number;
}

export interface BestScores {
  userBestScore: string;
  userLastScore: string;
  tournamentBestScore?: string;
  seats?: number;
  playerBestScore?: string;
}
export interface UserGameBestScores {
  userId: string;
  score: number;
  gameKey: string;
}

export enum TournamentType {
  CASH = 'cash',
  CASUAL = 'casual',
  PRIZE = 'prize',
  ALL = 'all',
}

export enum TournamentTagsEnum {
  PRIZE_TOURNAMENT = 'PRIZE TOURNAMENT',
  CASH_TOURNAMENT = 'CASH TOURNAMENT',
  CHARITY_EVENT = 'CHARITY EVENT',
  CELEBRITY_EVENT = 'CELEBRITY EVENT',
  ZBO_TOURNAMENT = 'ZBO TOURNAMENT',
  FREE_TOURNAMENT = 'FREE TOURNAMENT',
  CASUAL_TOURNAMENT = 'CASUAL TOURNAMENT',
}

export interface TournamentsFilters {
  active?: boolean;
  activeTournaments?: boolean;
  comingSoonOnly?: boolean;
  retiredEvents?: boolean;
  tournamentTags?: Array<TournamentTagsEnum>;
  gameType?: TournamentType;
  gameIds?: string[];
  cashPageIndex?: number;
  prizePageIndex?: number;
  cashPageSize?: number;
  prizePageSize?: number;
  pageIndex?: number;
  pageSize?: number;
}

export interface TournamentsPagination {
  leptTotal?: number;
  retiredTotal?: number;
  comingSoonTotal?: number;
  total: number;
  pageIndex: number;
  pagesLoaded: number[];
  pageSize: number;
  itemsLen?: number;
}

export interface AllTournamentsPagination {
  cashTournaments: TournamentsPagination;
  prizeTournaments: TournamentsPagination;
  total: number;
}

export interface TournamentsQueryParams extends TournamentsFilters {
  tournamentType: TournamentType;
  advertiseTournaments?: boolean;
  prizeValueRanges?: string[];
  entryFeeRanges?: string[];
  search?: string;
  clearCache?: boolean;
  getEsRanges?: boolean;
  sortBy?: string;
}

export interface TournamentsResponse extends TournamentsPagination {
  items: CasualTournamentEntity[];
  filters: TournamentsFilters;
  existingGameIds: Game[];
  eventsCountByTag: EventsCountByTag[];
  gamesByEventsList: Game[];
  esRangeValues: EsRangeValues;
  facets: Record<string, FaceItemModel[]>;
}

export interface ExistingGame {
  total: number;
  existingGameIds?: Game[];
}

export interface EventsCountByTag {
  tag: string;
  epTagTotal: string;
}

export interface EsRangeValues {
  entryAmountRanges: RangeValues[];
  prizeValueRanges: RangeValues[];
}

export interface RangeValues {
  min: number;
  max: number;
  values: number[];
}

export interface TournamentPasses {
  configured: ConfiguredPasses[];
  userPasses: UserPasses[];
}

export interface PromoCodeMetadata {
  uuid: string;
  percent?: number;
  canBeUsed?: boolean;
  redeemDate?: Date | null | undefined;
}

export interface ConfiguredPasses {
  uuid: string;
  name: string;
  entries: number;
  price: {
    price: string;
    promoCodes?: PromoCodeMetadata[];
  };
  description: string;
  archived: boolean;
  createdAt: Date;
  updatedAt: Date;
  purchased?: boolean;
  passIndex: number;
  purchaseLimitReached?: boolean;
}

export interface PassUsage {
  canBeUsed: boolean;
  redeemDate: Date | null | undefined;
}

export interface UserPasses {
  uuid: string;
  name: string;
  entries: number;
  price: {
    price: string;
    promoCodes?: PromoCodeMetadata[];
  };
  eventPrototypeUuid: string;
  eventScheduleUuid: string;
  fantazPassUuid: string;
  userUuid: string;
  activeCount: number;
  createdAt: Date;
  updatedAt: Date;
  purchased?: boolean;
  passIndex: number;
}

export interface TournamentPass extends ConfiguredPasses {
  eventPrototypeUuid?: string;
  eventScheduleUuid?: string;
  fantazPassUuid?: string;
  userUuid?: string;
  activeCount?: number;

  /**
   * Property to indicate if the pass can be buy
   */
  canBeBuy?: boolean;

  /**
   * Property to indicate if the pass is buying by the user
   */
  processing?: boolean;
  disabled?: boolean;
  purchased?: boolean;

  /**
   * UI Property to indicate if the pass is used to start the game
   */
  startPlay?: boolean;
}

export interface BuyPassRequest {
  eventPrototypeUuid: string;
  fantazPassUuid: string;
  tournamentName: string;
  gameName?: string;
  eventScheduleUuid: string;
  price: number;
  promoCodes?: PromoCodeMetadata[];
  passIndex: number;
}

export interface buyPassResponse {
  playerPass: UserPasses;
}
